import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IProduct } from 'src/app/models/products/product';
import { DownloadOutputService } from 'src/app/services/download-output.service';
import { AlertService } from 'src/app/services/alert.service';
import {
  AssetLabelSummary,
  LabelDataStatusResult,
  ProductLabelStatus,
} from 'src/app/models/download-output/download-output-result';
import { DialogResult } from 'src/app/models/dialog-result';

export interface LabelDataReviewDialogData {
  productIds: string[];
  products: IProduct[];
}

@Component({
  selector: 'app-label-data-product-create',
  templateUrl: './label-data-product-create.component.html',
  styleUrls: ['./label-data-product-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class LabelDataProductCreateComponent implements OnInit, OnDestroy {
  readonly DialogResult = DialogResult;
  isLoading = true;
  showSkipConfirmation = false;
  productStatuses: ProductLabelStatus[] = [];
  expandedProducts: Set<string> = new Set();
  expandedAssets: Set<string> = new Set();
  pendingAssets: Set<string> = new Set();
  highlightedAssets: Set<string> = new Set();
  highlightedProducts: Set<string> = new Set();
  highlightTimers: Subscription[] = [];
  pollSubscription: Subscription | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LabelDataReviewDialogData,
    public dialogRef: MatDialogRef<LabelDataProductCreateComponent>,
    private downloadOutputService: DownloadOutputService,
    private alertService: AlertService,
  ) {}

  // --- Lifecycle ---

  ngOnInit(): void {
    this.loadStatus();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.highlightTimers.forEach((s) => s.unsubscribe());
  }

  // --- Data loading ---

  loadStatus(): void {
    this.downloadOutputService.getLabelDataStatus(this.data.productIds).subscribe({
      next: (result: LabelDataStatusResult) => {
        this.updateStatuses(result);
        this.isLoading = false;
        this.checkPendingAssets();
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  refreshStatus(): void {
    const previousLabeled = this.getLabeledAssetIds();
    this.downloadOutputService.getLabelDataStatus(this.data.productIds).subscribe({
      next: (result: LabelDataStatusResult) => {
        this.updateStatuses(result);
        this.highlightNewLabels(previousLabeled);
      },
    });
  }

  private updateStatuses(result: LabelDataStatusResult): void {
    this.productStatuses = result.products;
  }

  // --- Status helpers ---

  hasValidLabel(asset: AssetLabelSummary): boolean {
    return asset.hasLabel && asset.label != null;
  }

  labeledCount(product: ProductLabelStatus): number {
    return product.assetLabels.filter((a) => this.hasValidLabel(a)).length;
  }

  productStatus(product: ProductLabelStatus): 'green' | 'orange' | 'red' {
    if (this.labeledCount(product) > 0) return 'green';
    if (product.assetLabels.some((a) => a.hasLabel)) return 'orange';
    return 'red';
  }

  get productsWithLabels(): number {
    return this.productStatuses.filter((p) => this.labeledCount(p) > 0).length;
  }

  get someHaveLabels(): boolean {
    return this.productStatuses.some((p) => this.labeledCount(p) > 0);
  }

  get allHaveAtLeastOneLabel(): boolean {
    return this.productStatuses.every((p) => this.labeledCount(p) > 0);
  }

  get allComplete(): boolean {
    return this.productStatuses.every((p) => this.productStatus(p) === 'green');
  }

  get skippedProducts(): ProductLabelStatus[] {
    return this.productStatuses.filter((p) => this.labeledCount(p) === 0);
  }

  get processableProducts(): ProductLabelStatus[] {
    return this.productStatuses.filter((p) => this.labeledCount(p) > 0);
  }

  // --- Expand / Collapse ---

  get anyExpanded(): boolean {
    return this.expandedProducts.size > 0;
  }

  toggleProduct(productId: string): void {
    this.clearHighlightsForProduct(productId);
    if (this.expandedProducts.has(productId)) {
      this.expandedProducts.delete(productId);
    } else {
      this.expandedProducts.add(productId);
    }
  }

  isProductExpanded(productId: string): boolean {
    return this.expandedProducts.has(productId);
  }

  toggleAsset(postAssetId: string): void {
    if (this.expandedAssets.has(postAssetId)) {
      this.expandedAssets.delete(postAssetId);
    } else {
      this.expandedAssets.add(postAssetId);
    }
  }

  isAssetExpanded(postAssetId: string): boolean {
    return this.expandedAssets.has(postAssetId);
  }

  expandAll(): void {
    this.productStatuses.forEach((p) => this.expandProduct(p));
  }

  collapseAll(): void {
    this.expandedProducts = new Set();
    this.expandedAssets = new Set();
  }

  expandProduct(product: ProductLabelStatus): void {
    this.expandedProducts.add(product.productId);
    product.assetLabels
      .filter((a) => this.hasValidLabel(a))
      .forEach((a) => this.expandedAssets.add(a.postAssetId));
  }

  collapseProduct(productId: string): void {
    this.expandedProducts.delete(productId);
    const product = this.findProduct(productId);
    if (product) {
      const assetIds = new Set(product.assetLabels.map((a) => a.postAssetId));
      this.expandedAssets = new Set([...this.expandedAssets].filter((id) => !assetIds.has(id)));
    }
  }

  anyAssetsExpanded(product: ProductLabelStatus): boolean {
    return product.assetLabels.some((a) => this.expandedAssets.has(a.postAssetId));
  }

  toggleProductAssets(product: ProductLabelStatus): void {
    if (this.anyAssetsExpanded(product)) {
      this.collapseProduct(product.productId);
    } else {
      this.expandProduct(product);
    }
  }

  // --- Polling ---

  onAssetExtracted(postAssetId: string): void {
    this.pendingAssets.add(postAssetId);
    this.startPolling();
  }

  private startPolling(attempt: number = 0): void {
    this.stopPolling();
    const maxAttempts = 8;
    const delayMs = Math.min(2000 * Math.pow(2, attempt), 30000);

    if (attempt >= maxAttempts) {
      this.pendingAssets = new Set();
      return;
    }

    this.pollSubscription = timer(delayMs).pipe(
      switchMap(() => this.downloadOutputService.getLabelDataStatus(this.data.productIds)),
    ).subscribe({
      next: (result: LabelDataStatusResult) => {
        this.updateStatuses(result);
        this.checkPendingAssets();
        if (this.pendingAssets.size > 0) {
          this.startPolling(attempt + 1);
        }
      },
    });
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }

  private checkPendingAssets(): void {
    const resolved = [...this.pendingAssets].filter((id) =>
      this.productStatuses.some((p) =>
        p.assetLabels.some((a) => a.postAssetId === id && this.hasValidLabel(a)),
      ),
    );
    resolved.forEach((id) => {
      this.pendingAssets.delete(id);
      this.highlightAsset(id);
    });
  }

  // --- Highlighting ---

  isHighlighted(postAssetId: string): boolean {
    return this.highlightedAssets.has(postAssetId);
  }

  isProductHighlighted(productId: string): boolean {
    return this.highlightedProducts.has(productId);
  }

  private highlightNewLabels(previousLabeled: Set<string>): void {
    this.productStatuses.forEach((p) =>
      p.assetLabels
        .filter((a) => this.hasValidLabel(a) && !previousLabeled.has(a.postAssetId))
        .forEach((a) => this.highlightAsset(a.postAssetId)),
    );
  }

  private highlightAsset(postAssetId: string): void {
    this.highlightedAssets.add(postAssetId);
    const product = this.findProductForAsset(postAssetId);
    if (product) {
      this.highlightedProducts.add(product.productId);
    }
    const sub = timer(60000).subscribe(() => {
      this.highlightedAssets.delete(postAssetId);
      if (product) {
        const stillHighlighted = product.assetLabels.some((a) => this.highlightedAssets.has(a.postAssetId));
        if (!stillHighlighted) {
          this.highlightedProducts.delete(product.productId);
        }
      }
    });
    this.highlightTimers = [...this.highlightTimers, sub];
  }

  private clearHighlightsForProduct(productId: string): void {
    this.highlightedProducts.delete(productId);
    const product = this.findProduct(productId);
    if (product) {
      product.assetLabels.forEach((a) => this.highlightedAssets.delete(a.postAssetId));
    }
  }

  private getLabeledAssetIds(): Set<string> {
    return new Set(
      this.productStatuses.flatMap((p) =>
        p.assetLabels.filter((a) => this.hasValidLabel(a)).map((a) => a.postAssetId),
      ),
    );
  }

  // --- Navigation ---

  getPostAssetUrl(product: ProductLabelStatus, asset: AssetLabelSummary): string {
    return `/batches/${product.batchId}/products/${product.productId}/productTouchAsset/${asset.productTouchAssetId}/masterSuiteTouchAsset/${asset.masterSuiteTouchAssetId}/post-asset?section=labels`;
  }

  // --- Actions ---

  onGenerate(): void {
    if (this.skippedProducts.length > 0) {
      this.showSkipConfirmation = true;
      return;
    }
    this.executeGenerate();
  }

  confirmGenerate(): void {
    this.executeGenerate();
  }

  cancelConfirmation(): void {
    this.showSkipConfirmation = false;
  }

  private executeGenerate(): void {
    const productIds = this.processableProducts.map((p) => p.productId);
    this.downloadOutputService
      .mergeProductLabels(productIds)
      .subscribe({
        next: (result) => {
          this.alertService.successToast(
            `Success! ${result.productProcessingCount}/${result.productCount} Products Queued! Product data will be ready shortly!`,
          );
          this.dialogRef.close(DialogResult.Success);
        },
      });
  }

  // --- Helpers ---

  private findProduct(productId: string): ProductLabelStatus | undefined {
    return this.productStatuses.find((p) => p.productId === productId);
  }

  private findProductForAsset(postAssetId: string): ProductLabelStatus | undefined {
    return this.productStatuses.find((p) =>
      p.assetLabels.some((a) => a.postAssetId === postAssetId),
    );
  }
}
